/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
// Coupon stacking + formatting helpers shared by the plan / checkout screens.
//
// The /plans/auto-apply-coupon endpoint returns:
//   - `coupon`          — house auto-apply coupon ({ discountType, discountValue })
//   - `assignedCoupons` — per-user reserved codes [authenticated only]; the user
//                         picks one to apply as a manual coupon code at checkout.
// A manually-validated coupon ({ type, value }) — whether typed or chosen from
// `assignedCoupons` — is mutually exclusive with the house coupon and stacks on
// top of it. These helpers drive the display-only pre-purchase estimate; the
// authoritative total comes from the API.

export type CouponLike =
  | {
      type?: 'percent' | 'amount' | string
      value?: string | number
      discountType?: 'percent' | 'amount' | string
      discountValue?: string | number
    }
  | null
  | undefined

const couponKind = (c: NonNullable<CouponLike>) => c.type ?? c.discountType
const couponValue = (c: NonNullable<CouponLike>) => Number(c.value ?? c.discountValue)

// Apply a single coupon (percent or fixed-amount) to an amount in major units.
// Missing / zero / invalid coupons are a no-op. Result is clamped at 0.
export const applyCouponToAmount = (amount: number, coupon: CouponLike): number => {
  if (!coupon) return amount
  const value = couponValue(coupon)
  if (!isFinite(value) || value <= 0) return amount
  const next = couponKind(coupon) === 'percent' ? amount - (amount * value) / 100 : amount - value
  return Math.max(0, next)
}

const couponCode = (c: NonNullable<CouponLike>) =>
  typeof (c as { code?: unknown }).code === 'string'
    ? ((c as { code: string }).code).trim().toUpperCase()
    : ''

// Apply a list of coupons in order, compounding each on the running amount.
// Coupons sharing a code are applied only once — the backend dedups the same
// coupon (e.g. a manual code equal to the house auto-apply), so the displayed
// estimate must match. Order matters: pass [house, manual, assigned].
export const stackCoupons = (amount: number, coupons: CouponLike[]): number => {
  const seen = new Set<string>()
  return coupons.reduce((total, coupon) => {
    if (!coupon) return total
    const code = couponCode(coupon)
    if (code && seen.has(code)) return total
    if (code) seen.add(code)
    return applyCouponToAmount(total, coupon)
  }, amount)
}

// Badge label, e.g. "25% off" / "$10.00 off".
export const formatCouponDiscount = (coupon: CouponLike): string => {
  if (!coupon) return ''
  const value = couponValue(coupon)
  return couponKind(coupon) === 'percent' ? `${value}% off` : `$${value.toFixed(2)} off`
}

// Right-aligned short amount, e.g. "25%" / "$10.00" (caller prefixes the minus).
export const formatCouponAmount = (coupon: CouponLike): string => {
  if (!coupon) return ''
  const value = couponValue(coupon)
  return couponKind(coupon) === 'percent' ? `${value}%` : `$${value.toFixed(2)}`
}
